// Claude: Admin Users page — change own password + create new admin user 02-03-2026
import React, { useState } from "react";
import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import { AuthAPI } from "../../Apis/authAPI";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

type Props = {};

const AdminUsers = (_props: Props) => {
  const { success, error } = useToastWrapper();

  // Change password state
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  // Create admin user state
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      error("All password fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      error("New password and confirm password do not match");
      return;
    }
    if (newPassword.length < 6) {
      error("New password must be at least 6 characters");
      return;
    }
    setPwLoading(true);
    try {
      await AuthAPI.postUpdatePassword({ oldPassword, newPassword });
      success("Password changed successfully");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setPwLoading(false);
    }
  };

  const handleCreateAdminUser = async () => {
    if (!newAdminEmail.trim()) {
      error("Email is required");
      return;
    }
    setCreateLoading(true);
    try {
      await AdminAPI.createAdminUser(newAdminEmail.trim().toLowerCase());
      success("Admin user created. A password setup link has been sent to their email.");
      setNewAdminEmail("");
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setCreateLoading(false);
    }
  };

  const inputStyle = {
    border: "1px solid",
    outline: "none",
    h: "32px",
    px: 2,
    fontSize: "sm",
    borderColor: "gray.300",
    _focus: { outline: "none", border: "1px solid", borderColor: "gray.500" },
    maxW: "320px",
  };

  const sectionBoxStyle = {
    p: 4,
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "md",
    maxW: "420px",
    mb: 6,
  };

  return (
    <Box p={4}>
      <Text fontSize="lg" fontWeight={600} mb={4}>Admin Users</Text>

      {/* Change My Password */}
      <Box {...sectionBoxStyle}>
        <Text fontSize="md" fontWeight={600} mb={3}>Change My Password</Text>
        <Flex direction="column" gap={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Current Password</Text>
            <Input
              {...inputStyle}
              type="password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              placeholder="Enter current password"
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>New Password</Text>
            <Input
              {...inputStyle}
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Confirm New Password</Text>
            <Input
              {...inputStyle}
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </Box>
          <Box>
            <Button
              size="sm"
              fontSize="xs"
              h="32px"
              px={5}
              background="#0E122F"
              color="white"
              _hover={{ background: "#2E7D32" }}
              isLoading={pwLoading}
              onClick={handleChangePassword}
            >
              Update Password
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Create New Admin User */}
      <Box {...sectionBoxStyle}>
        <Text fontSize="md" fontWeight={600} mb={1}>Create Admin User</Text>
        <Text fontSize="xs" color="gray.500" mb={3}>
          A password setup link will be sent to the new admin's email address.
          Admin accounts cannot be deleted via the UI.
        </Text>
        <Flex direction="column" gap={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Email Address</Text>
            <Input
              {...inputStyle}
              type="email"
              value={newAdminEmail}
              onChange={e => setNewAdminEmail(e.target.value)}
              placeholder="admin@example.com"
            />
          </Box>
          <Box>
            <Button
              size="sm"
              fontSize="xs"
              h="32px"
              px={5}
              background="#0E122F"
              color="white"
              _hover={{ background: "#2E7D32" }}
              isLoading={createLoading}
              onClick={handleCreateAdminUser}
            >
              Create Admin User
            </Button>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default AdminUsers;
